import * as vp from 'vopay';
import * as Yup from 'yup';
import { FunctionComponent } from 'react';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useLayout } from '../../../../../providers/Layout.provider';
import { usePaylink } from '../../../../../providers/Paylink.provider';
import styles from './MultiplePaymentMethodsDialogue.module.scss';

const { Box, TextField, Button, ElementContainer, ErrorText, Icon } = vp;

type MultiplePaymentMethodsFormProps = {
  defaultValues: {
    Amount: string;
    MaximumAmount: string;
  };
  onSave: (values: { Amount: string; MaximumAmount: string }) => void;
};

type FC = FunctionComponent<MultiplePaymentMethodsFormProps>;

const MultiplePaymentMethodsForm: FC = ({ defaultValues, onSave }: MultiplePaymentMethodsFormProps) => {
  const { color } = useLayout();
  const { paylink } = usePaylink();
  const { t } = useTranslation('translations', { keyPrefix: 'MultiplePaymentMethods' });
  return (
    <Formik
      initialValues={defaultValues}
      validationSchema={createdValidationSchema()}
      validateOnMount
      enableReinitialize
      onSubmit={onSave}
    >
      {({ values, touched, errors, handleSubmit, isValid, getFieldProps }) => (
        <form onSubmit={handleSubmit}>
          <Box flx="col" gap="lg" base="8">
            <Box flx="col">
              <ElementContainer
                isBold={false}
                padding="0 0 6px 0"
                label={
                  <Box ai="fs" jc="fs">
                    {t('enterAmount')} <Icon icon="asterisk" height="20px" width="10px" color="error" />
                  </Box>
                }
                input={
                  <Box flx="col" gap="sm" base="8">
                    <TextField
                      variant="lined"
                      preset="currency"
                      {...getFieldProps('Amount')}
                      elementLeft={<div className={styles.Currency}>{paylink.Currency}</div>}
                      borderColor={errors.Amount && (touched.Amount || !!values.Amount) ? '#E24C4C' : '#C4C4C4'}
                      placeholder="0.00"
                    />
                    {errors.Amount && (touched.Amount || !!values.Amount) && (
                      <ErrorText
                        size="md"
                        error={errors.Amount && (touched.Amount || !!values.Amount) ? errors.Amount : null}
                        fitContent
                      />
                    )}
                  </Box>
                }
              />
            </Box>
            <Box flx="col">
              <Button
                id="flexible-payment-continue-btn"
                label="Continue"
                type="submit"
                width="100%"
                height="48px"
                color="primary"
                background={color}
                textColor={vp.getContrastColor(color)}
                round
                bold
                noShadow
                opacity={isValid ? '1' : '0.5'}
                isDisabled={!isValid}
              />
            </Box>
          </Box>
        </form>
      )}
    </Formik>
  );

  function createdValidationSchema() {
    return Yup.object().shape({
      Amount: Yup.number()
        .required(`${t('requiredAmountError')}`) // Optional: Custom error message for required field
        .transform((_value, originalValue: string) => Number(vp.sanitizeToNumeric(originalValue)))
        .max(
          Number(defaultValues.MaximumAmount),
          `${t('exceedsMaximumAmountError', { max: Number(defaultValues.MaximumAmount) })}`,
        )
        .min(0.01, `${t('minimumAmountError')}`),
    });
  }
};

export default MultiplePaymentMethodsForm;
